import __request__ from 'routing/request';
interface DataLakeArchive {
    readonly timestamp_to: string;
    readonly last_known_stream_title: string;
    readonly archive_config_id: string;
    readonly stream_id: string;
    readonly id: string;
    readonly message_count: number;
    readonly timestamp_from: string;
    readonly restore_history: DataLakeRetrievalResult[];
}
interface DataLakeMessage {
    readonly id: string;
    readonly timestamp: string;
}
interface FilterOption {
    readonly title: string;
    readonly value: string;
}
interface PaginationParameters {
    readonly per_page: number;
    readonly query: string;
    readonly page: number;
    readonly sort_by: string;
    readonly order: string;
}
interface DataLakeStream {
    readonly last_known_stream_title: string;
    readonly stream_id: string;
    readonly id: string;
}
interface DataLakeRetrievalResult__RestoreIndex {
    readonly index_set_id: string;
    readonly index_name: string;
}
interface DataLakeResource__GetStreamConfigsRequest {
    readonly stream_ids: string[];
}
interface MultipleMessagesRetrievalRequest {
    readonly messages: DataLakeMessage[];
    readonly type: string;
    readonly data_lake_id: string;
    readonly timeout: number;
}
interface SizeEstimatesOverview {
    readonly size_estimates: {
        readonly [_key: string]: SizeEstimate;
    };
    readonly total_size_bytes: number;
    readonly total_files_count: number;
    readonly from: string;
    readonly to: string;
    readonly row_count: number;
}
interface DataLakeDeletionBulkRequest {
    readonly stream_ids: string[];
}
interface PaginatedList__PaginationInfo {
    readonly per_page: number;
    readonly total: number;
    readonly count: number;
    readonly page: number;
}
interface DataLakeArchivingConfiguration {
    readonly archive_name: string;
    readonly stream_id: string;
    readonly retention_time: string;
    readonly id: string;
    readonly enabled: boolean;
}
type DataLakeArchivingConfigurationArray = DataLakeArchivingConfiguration[];
interface RetrievalFilter {
    readonly value: Object;
    readonly field_name: string;
}
interface SizeEstimate {
    readonly size_bytes: number;
    readonly files_count: number;
    readonly archive_name: string;
    readonly table_identifier: string;
    readonly row_count: number;
}
interface PageListResponse_DataLakeArchive {
    readonly total: number;
    readonly pagination: PaginatedList__PaginationInfo;
    readonly defaults: EntityDefaults;
    readonly query: string;
    readonly elements: DataLakeArchive[];
    readonly attributes: EntityAttribute[];
    readonly sort: string;
    readonly order: 'asc' | 'desc';
}
type stringArray = string[];
interface StreamDataLakeStatus {
    readonly stream_id: string;
    readonly enabled: boolean;
}
interface DataLakeResource__GetStreamStatusesRequest {
    readonly stream_ids: string[];
}
interface anyMap {
    readonly [_key: string]: unknown;
}
interface MultipleMessagesAddToInvestigationRequest {
    readonly investigation_ids: string[];
    readonly retrieval_request: MultipleMessagesRetrievalRequest;
}
interface RetentionTimeChangeRequest {
    readonly retention_time: string;
}
interface DataLakeRetrievalResult {
    readonly timestamp_to: string;
    readonly restore_indices: DataLakeRetrievalResult__RestoreIndex[];
    readonly retrieved_documents: number;
    readonly index_failures: number;
    readonly retrieved_at: string;
    readonly type: 'ICEBERG' | 'TRINO' | 'ATHENA';
    readonly timestamp_from: string;
    readonly duration: string;
    readonly stream_id: string;
    readonly stream_title: string;
    readonly id: string;
    readonly partially_retrieved: boolean;
    readonly source_name: string;
    readonly username: string;
    readonly status: 'COMPLETE' | 'PARTIAL';
}
interface EntityAttribute {
    readonly filterable: boolean;
    readonly related_property: string;
    readonly related_collection: string;
    readonly hidden: boolean;
    readonly sortable: boolean;
    readonly id: string;
    readonly type: 'STRING' | 'DATE' | 'DOUBLE' | 'INT' | 'LONG' | 'OBJECT_ID' | 'BOOLEAN';
    readonly title: string;
    readonly searchable: boolean;
    readonly filter_options: FilterOption[];
}
interface PageListResponse_DataLakeStream {
    readonly total: number;
    readonly pagination: PaginatedList__PaginationInfo;
    readonly defaults: EntityDefaults;
    readonly query: string;
    readonly elements: DataLakeStream[];
    readonly attributes: EntityAttribute[];
    readonly sort: string;
    readonly order: 'asc' | 'desc';
}
type StreamDataLakeStatusArray = StreamDataLakeStatus[];
interface DataLakeConfigEnableRequest {
    readonly stream_ids: string[];
    readonly enabled: boolean;
}
interface Sorting {
    readonly id: string;
    readonly direction: 'ASC' | 'DESC';
}
type RetrievalFilterArray = RetrievalFilter[];
interface EntityDefaults {
    readonly sort: Sorting;
}
interface RetrievalBulkRequest {
    readonly field_filters: RetrievalFilter[];
    readonly type: string;
    readonly data_lake_id: string;
    readonly operator: 'AND' | 'OR';
}
/**
 * Returns all archives
 */
export function getArchives(paginationparameters?: PaginationParameters): Promise<PageListResponse_DataLakeArchive> {
    return __request__('GET', '/plugins/org.graylog.plugins.datalake/data_lake', paginationparameters, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Delete retrieved Archive
 */
export function deleteRestoredArchive(streamId: string, restoredOperationId: string): Promise<unknown> {
    return __request__('DELETE', `/plugins/org.graylog.plugins.datalake/data_lake/archive/${streamId}/retrieved/${restoredOperationId}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Restores the messages requested and adds them to an investigation
 */
export function addMultipleMessagesToInvestigation(request?: MultipleMessagesAddToInvestigationRequest): Promise<unknown> {
    return __request__('POST', '/plugins/org.graylog.plugins.datalake/data_lake/investigations/add_data_lake_messages', request, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Delete data from the requested Archives
 */
export function remove(stream_ids: DataLakeDeletionBulkRequest = { "stream_ids": [] }, from?: string, to?: string, full_delete?: boolean, wipe_restores?: boolean): Promise<unknown> {
    return __request__('POST', '/plugins/org.graylog.plugins.datalake/data_lake/jobs/delete', stream_ids, { 'from': from, 'to': to, 'full_delete': full_delete, 'wipe_restores': wipe_restores }, {
        'Accept': ['application/json']
    });
}
/**
 * Get archive progress
 */
export function progress(): Promise<anyMap> {
    return __request__('GET', '/plugins/org.graylog.plugins.datalake/data_lake/jobs/progress', null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get job progresses for a specific stream id
 */
export function progressBystreamId(streamId: string): Promise<anyMap> {
    return __request__('GET', `/plugins/org.graylog.plugins.datalake/data_lake/jobs/progress/${streamId}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Restores the requested Archives
 */
export function retrieveBulkRetrievalRequest(retrieval_bulk_request?: RetrievalBulkRequest, from?: string, to?: string): Promise<unknown> {
    return __request__('POST', '/plugins/org.graylog.plugins.datalake/data_lake/jobs/retrieve', retrieval_bulk_request, { 'from': from, 'to': to }, {
        'Accept': ['application/json']
    });
}
/**
 * Restores the messages requested
 */
export function retrieveMultipleMessages(request?: MultipleMessagesRetrievalRequest): Promise<unknown> {
    return __request__('POST', '/plugins/org.graylog.plugins.datalake/data_lake/jobs/retrieve_messages_by_id', request, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Cancel specified Data Lake job
 */
export function cancel(jobId: string): Promise<unknown> {
    return __request__('DELETE', `/plugins/org.graylog.plugins.datalake/data_lake/jobs/${jobId}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get an archive estimate overview for a list of streams
 */
export function restoreEstimate(stream_ids: stringArray, from: string, to: string, data_lake_id?: string, inclusion_type: 'DATA_LAKE_ONLY' | 'INDEXER_ONLY' | 'BOTH' = 'BOTH', field_filters?: RetrievalFilterArray, operator: 'AND' | 'OR' = 'AND'): Promise<SizeEstimatesOverview> {
    return __request__('POST', '/plugins/org.graylog.plugins.datalake/data_lake/size_estimate', field_filters, { 'data_lake_id': data_lake_id, 'stream_ids': stream_ids, 'from': from, 'to': to, 'inclusion_type': inclusion_type, 'operator': operator }, {
        'Accept': ['application/json']
    });
}
/**
 * Returns all Data Lake streams
 */
export function getDataLakeStreams(paginationparameters?: PaginationParameters): Promise<PageListResponse_DataLakeStream> {
    return __request__('GET', '/plugins/org.graylog.plugins.datalake/data_lake/stream', paginationparameters, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get Data Lake configurations for given stream ids
 */
export function getStreamConfigs(streamId: DataLakeResource__GetStreamConfigsRequest): Promise<DataLakeArchivingConfigurationArray> {
    return __request__('POST', '/plugins/org.graylog.plugins.datalake/data_lake/stream/config', streamId, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Set enabled status for a list of configuration ids
 */
export function toggleConfigEnableStatus(enable: DataLakeConfigEnableRequest = { "stream_ids": [], "enabled": true }): Promise<void> {
    return __request__('POST', '/plugins/org.graylog.plugins.datalake/data_lake/stream/config/enable', enable, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get Data Lake enabled statuses for a list of stream ids
 */
export function getEnabledStreamStatuses(stream_ids: DataLakeResource__GetStreamStatusesRequest): Promise<StreamDataLakeStatusArray> {
    return __request__('POST', '/plugins/org.graylog.plugins.datalake/data_lake/stream/status', stream_ids, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get Data Lake archive for a stream id
 */
export function getDataLakeArchive(streamId?: string): Promise<DataLakeArchive> {
    return __request__('GET', `/plugins/org.graylog.plugins.datalake/data_lake/stream/${streamId}/archive`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get Data Lake configuration for a stream id
 */
export function getStreamConfig(streamId?: string): Promise<DataLakeArchivingConfiguration> {
    return __request__('GET', `/plugins/org.graylog.plugins.datalake/data_lake/stream/${streamId}/config`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Optimize Data Lake files for the given stream.
 */
export function optimize(streamId?: string, action?: string, target_file_size_bytes: number = 0, retain_last_num_snapshots: number = 0, expire_snapshots_older_than?: string): Promise<void> {
    return __request__('POST', `/plugins/org.graylog.plugins.datalake/data_lake/stream/${streamId}/optimize/${action}`, null, { 'target_file_size_bytes': target_file_size_bytes, 'retain_last_num_snapshots': retain_last_num_snapshots, 'expire_snapshots_older_than': expire_snapshots_older_than }, {
        'Accept': ['application/json']
    });
}
/**
 * Overwrites the global retention configuration for a specific stream
 */
export function setArchiveRetentionTime(streamId?: string, retention_time_change_request?: RetentionTimeChangeRequest): Promise<void> {
    return __request__('POST', `/plugins/org.graylog.plugins.datalake/data_lake/stream/${streamId}/retention_time`, retention_time_change_request, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get Data Lake enabled status for a stream id
 */
export function getEnabledStatus(streamId?: string): Promise<StreamDataLakeStatus> {
    return __request__('GET', `/plugins/org.graylog.plugins.datalake/data_lake/stream/${streamId}/status`, null, {}, {
        'Accept': ['application/json']
    });
}
